import { normalizeTrack } from '@/state/trackModel';

const ALLOWED_BARS = [1, 2, 4, 8];
const ROW_IDS = ['kick', 'snare', 'clap', 'hat', 'openhat', 'perc', 'rim', 'crash'];

const clamp = (value, min, max, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};

export const clonePattern = (pattern) => structuredClone(pattern);
export const supportsPianoRoll = (patternType) => patternType === 'keys' || patternType === 'sampler';

export function patternBarsOf(pattern) {
  const requested = Number(pattern?.patternBars);
  return ALLOWED_BARS.includes(requested) ? requested : 1;
}

function sanitizeDrums(pattern = {}) {
  const patternBars = patternBarsOf(pattern);
  const stepsPerBar = Number(pattern.stepsPerBar) === 12 ? 12 : 16;
  const length = patternBars * stepsPerBar;
  const incomingRows = new Map((Array.isArray(pattern.rows) ? pattern.rows : []).filter((row) => row && ROW_IDS.includes(row.rowId)).map((row) => [row.rowId, row]));
  const rows = ROW_IDS.map((rowId) => {
    const row = incomingRows.get(rowId) || {};
    return { rowId, soundId: typeof row.soundId === 'string' ? row.soundId : null, volume: clamp(row.volume, 0, 1, 1), muted: Boolean(row.muted) };
  });
  const steps = {};
  const ratchets = {};
  for (const row of rows) {
    const source = Array.isArray(pattern.steps?.[row.rowId]) ? pattern.steps[row.rowId] : [];
    steps[row.rowId] = Array.from({ length }, (_, index) => {
      const velocity = Number(source[index]);
      return velocity > 0 ? clamp(velocity, 0.1, 1, 1) : 0;
    });
    const rowRatchets = pattern.ratchets?.[row.rowId];
    ratchets[row.rowId] = {};
    if (rowRatchets && typeof rowRatchets === 'object') {
      for (const [indexText, count] of Object.entries(rowRatchets)) {
        const index = Number(indexText);
        if (Number.isInteger(index) && index >= 0 && index < length && [2, 3, 4].includes(Number(count))) {
          ratchets[row.rowId][index] = Number(count);
        }
      }
    }
  }
  return {
    patternBars,
    stepsPerBar,
    kitId: typeof pattern.kitId === 'string' ? pattern.kitId : null,
    grooveId: typeof pattern.grooveId === 'string' ? pattern.grooveId : null,
    rows,
    steps,
    ratchets,
    swing: clamp(pattern.swing, 0, 0.6, 0),
  };
}

function sanitizeNotes(pattern = {}) {
  const patternBars = patternBarsOf(pattern);
  const maxBeats = patternBars * 4;
  const notes = (Array.isArray(pattern.notes) ? pattern.notes : []).filter((note) => {
    const start = Number(note?.start);
    const length = Number(note?.length);
    const pitch = Number(note?.pitch);
    return Number.isFinite(start) && start >= 0 && start < maxBeats && Number.isFinite(length) && length > 0
      && Number.isInteger(pitch) && pitch >= 0 && pitch <= 127;
  }).map((note, index) => ({
    id: typeof note.id === 'string' ? note.id : `note-${index}`,
    start: Number(note.start),
    length: Math.min(Number(note.length), maxBeats - Number(note.start)),
    pitch: Number(note.pitch),
    velocity: clamp(note.velocity, 0.1, 1, 0.8),
  }));
  const chords = (Array.isArray(pattern.chords) ? pattern.chords : []).filter((chord) => Number.isInteger(Number(chord?.bar))
    && Number(chord.bar) >= 0 && Number(chord.bar) < patternBars && typeof chord.symbol === 'string')
    .map((chord) => ({ bar: Number(chord.bar), symbol: chord.symbol, roman: typeof chord.roman === 'string' ? chord.roman : '' }));
  const source = pattern.chordSource && typeof pattern.chordSource === 'object' ? pattern.chordSource : null;
  return {
    patternBars,
    notes,
    lockToKey: pattern.lockToKey !== false,
    chords,
    chordSource: source ? {
      packId: typeof source.packId === 'string' ? source.packId : null,
      family: typeof source.family === 'string' ? source.family : 'trap',
      optionName: typeof source.optionName === 'string' ? source.optionName : '',
      rhythm: typeof source.rhythm === 'string' ? source.rhythm : 'sustain',
    } : null,
    melodySeed: Number.isInteger(Number(pattern.melodySeed)) ? Number(pattern.melodySeed) : null,
    melodyRhythm: Array.isArray(pattern.melodyRhythm) ? pattern.melodyRhythm.map(Number).filter(Number.isFinite) : [],
    melodyStyle: ['hook', 'lead', 'counter'].includes(pattern.melodyStyle) ? pattern.melodyStyle : 'hook',
    mono: Boolean(pattern.mono),
    glide: clamp(pattern.glide, 0, 500, pattern.mono ? 80 : 0),
    reverb: clamp(pattern.reverb, 0, 1, 0),
    echo: clamp(pattern.echo, 0, 1, 0),
    attack: clamp(pattern.attack, 0.001, 0.5, 0.005),
    release: clamp(pattern.release, 0.01, 2, 0.15),
  };
}

/** Pure compatibility boundary for saved/imported pattern data. */
export function sanitizePattern(patternType, pattern = {}) {
  if (patternType === 'drums') return sanitizeDrums(pattern);
  const common = sanitizeNotes(pattern);
  if (patternType === 'sampler') return {
    ...common,
    sampleId: typeof pattern.sampleId === 'string' ? pattern.sampleId : null,
    rootNote: Math.round(clamp(pattern.rootNote, 0, 127, 60)),
    tune: clamp(pattern.tune, -12, 12, 0),
    fine: clamp(pattern.fine, -50, 50, 0),
    gate: pattern.gate !== false,
  };
  return { ...common, voice: ['keys', 'strings', '808', 'fingerbass', 'pluck'].includes(pattern.voice) ? pattern.voice : 'keys' };
}

export function makeDefaultDrumPattern(kit = null, patternBars = 1) {
  const kitRows = new Map((kit?.rows || []).map((row) => [row.id, row]));
  const rows = ROW_IDS.map((rowId) => ({
    rowId,
    soundId: kitRows.get(rowId)?.sounds?.[0]?.id || null,
    volume: 1,
    muted: false,
  }));
  return sanitizePattern('drums', { patternBars, kitId: kit?.id || null, rows, steps: {}, ratchets: {}, swing: 0 });
}

export function resizePattern(patternType, pattern, nextBars) {
  const old = sanitizePattern(patternType, pattern);
  const bars = ALLOWED_BARS.includes(Number(nextBars)) ? Number(nextBars) : old.patternBars;
  if (patternType !== 'drums') {
    const oldBeats = old.patternBars * 4;
    const nextBeats = bars * 4;
    const notes = [];
    for (let offset = 0; offset < nextBeats; offset += oldBeats) {
      for (const note of old.notes) {
        const start = note.start + offset;
        if (start >= nextBeats) continue;
        notes.push({ ...note, id: offset ? `${note.id}-repeat-${offset}` : note.id, start, length: Math.min(note.length, nextBeats - start) });
      }
      if (bars <= old.patternBars) break;
    }
    return sanitizePattern(patternType, { ...old, patternBars: bars, notes });
  }
  const nextLength = bars * old.stepsPerBar;
  const oldLength = old.patternBars * old.stepsPerBar;
  const steps = {};
  const ratchets = {};
  for (const row of old.rows) {
    steps[row.rowId] = Array.from({ length: nextLength }, (_, index) => old.steps[row.rowId][index % oldLength] || 0);
    ratchets[row.rowId] = {};
    for (let index = 0; index < nextLength; index += 1) {
      const count = old.ratchets[row.rowId]?.[index % oldLength];
      if (count) ratchets[row.rowId][index] = count;
    }
  }
  return sanitizePattern('drums', { ...old, patternBars: bars, steps, ratchets });
}

/** Move drum hits to the nearest time position on a new grid. Ties land earlier. */
export function convertDrumGrid(pattern, nextStepsPerBar) {
  const old = sanitizePattern('drums', pattern);
  const destinationGrid = Number(nextStepsPerBar) === 12 ? 12 : 16;
  if (destinationGrid === old.stepsPerBar) return { pattern: old, mergedHits: 0, mapping: [] };
  const length = old.patternBars * destinationGrid;
  const steps = Object.fromEntries(old.rows.map((row) => [row.rowId, Array(length).fill(0)]));
  const ratchets = Object.fromEntries(old.rows.map((row) => [row.rowId, {}]));
  const mapping = Array.from({ length: old.stepsPerBar }, (_, source) => source === 0 ? 0 : Math.ceil((source * destinationGrid / old.stepsPerBar) - 0.5));
  let sourceHits = 0;
  let destinationHits = 0;
  for (const row of old.rows) {
    for (let bar = 0; bar < old.patternBars; bar += 1) {
      for (let source = 0; source < old.stepsPerBar; source += 1) {
        const sourceIndex = bar * old.stepsPerBar + source;
        const velocity = old.steps[row.rowId][sourceIndex];
        if (!(velocity > 0)) continue;
        sourceHits += 1;
        const destinationIndex = bar * destinationGrid + mapping[source];
        if (!(steps[row.rowId][destinationIndex] > 0)) destinationHits += 1;
        steps[row.rowId][destinationIndex] = Math.max(steps[row.rowId][destinationIndex], velocity);
        const ratchet = old.ratchets[row.rowId]?.[sourceIndex];
        if (ratchet) ratchets[row.rowId][destinationIndex] = Math.max(ratchets[row.rowId][destinationIndex] || 1, ratchet);
      }
    }
  }
  return {
    pattern: sanitizePattern('drums', { ...old, stepsPerBar: destinationGrid, steps, ratchets }),
    mergedHits: sourceHits - destinationHits,
    mapping,
  };
}

export function createPatternClip({ type = 'drums', kit = null, sampleDefaults = null, startBar = 0, color = '#FF9F1C' } = {}) {
  const id = crypto.randomUUID();
  let pattern;
  if (type === 'drums') pattern = makeDefaultDrumPattern(kit);
  else if (type === 'sampler') pattern = sanitizePattern('sampler', { patternBars: 1, ...(sampleDefaults || {}) });
  else pattern = sanitizePattern('keys', { patternBars: 1, voice: 'keys' });
  return {
    id: `clip-${id}`,
    name: type === 'drums' ? 'Drum Pattern' : type === 'keys' ? 'Keys Pattern' : 'Sampler Pattern',
    color,
    start_bar: startBar,
    bars: 1,
    offset: 0,
    naturalLoopBars: 1,
    isWholeLoop: true,
    fade_in: 0,
    fade_out: 0,
    isPattern: true,
    audio_url: null,
    loop_id: `pattern-${id}`,
    patternType: type,
    pattern,
  };
}

export function linkedCount(tracks, loopId) {
  return (tracks || []).reduce((count, track) => count + (track.clips || []).filter((clip) => clip.isPattern && clip.loop_id === loopId).length, 0);
}

/** Apply one linked pattern edit, suitable for one pushState call. */
export function applyLinkedPattern(tracks, loopId, patternType, nextPattern) {
  const pattern = sanitizePattern(patternType, nextPattern);
  const patternBars = pattern.patternBars;
  return (tracks || []).map((track) => ({ ...track, clips: (track.clips || []).map((clip) => {
    if (!clip.isPattern || clip.loop_id !== loopId) return clip;
    const whole = clip.isWholeLoop !== false;
    const maxWindow = Math.max(0.0625, patternBars - Math.min(Number(clip.offset || 0), Math.max(0, patternBars - 0.0625)));
    return {
      ...clip,
      pattern: clonePattern(pattern),
      naturalLoopBars: patternBars,
      offset: Math.min(Number(clip.offset || 0), Math.max(0, patternBars - 0.0625)),
      bars: whole ? patternBars : Math.min(Number(clip.bars || maxWindow), maxWindow),
    };
  }) }));
}

export function makePatternUnique(tracks, clipId, uuid = () => crypto.randomUUID()) {
  return (tracks || []).map((track) => ({ ...track, clips: (track.clips || []).map((clip) => clip.id === clipId && clip.isPattern
    ? { ...clip, loop_id: `pattern-${uuid()}`, pattern: clonePattern(clip.pattern) }
    : clip) }));
}

/** Sanitize patterns and repair conflicting linked payloads. First track/start occurrence wins. */
export function normalizePatternTracks(tracks) {
  const winners = new Map();
  const ordered = (tracks || []).flatMap((track, trackIndex) => (track.clips || []).map((clip, clipIndex) => ({ clip, trackIndex, clipIndex })))
    .filter(({ clip }) => clip.isPattern)
    .sort((a, b) => a.trackIndex - b.trackIndex || Number(a.clip.start_bar || 0) - Number(b.clip.start_bar || 0) || a.clipIndex - b.clipIndex);
  for (const { clip } of ordered) {
    if (!winners.has(clip.loop_id)) winners.set(clip.loop_id, sanitizePattern(clip.patternType, clip.pattern));
  }
  return (tracks || []).map((incomingTrack) => {
    const track = normalizeTrack(incomingTrack);
    return ({ ...track, clips: (track.clips || []).map((clip) => {
    if (!clip.isPattern) return clip;
    const pattern = clonePattern(winners.get(clip.loop_id));
    const whole = clip.isWholeLoop !== false;
    const offset = Math.min(Number(clip.offset || 0), Math.max(0, pattern.patternBars - 0.0625));
    const maxWindow = Math.max(0.0625, pattern.patternBars - offset);
    return { ...clip, audio_url: null, pattern, naturalLoopBars: pattern.patternBars, offset, bars: whole ? pattern.patternBars : Math.min(Number(clip.bars || maxWindow), maxWindow) };
    }) });
  });
}
